import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import ZipcodeUpload from './ZipcodeUpload';

const UploadHistory = () => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const fetchUploadHistory = async () => {
    try {
      setError(null);
      setRefreshing(true);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      
      const response = await axios.get(
        `${apiUrl}/wms/api/zipcodes/uploads`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
          credentials: 'include'
        }
      );
      setUploads(response.data.data || []);
      setPage(0);
    } catch (error) {
      console.error('Error fetching upload history:', error);
      let errorMessage = 'Failed to fetch upload history.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Upload history endpoint not found. The server might be misconfigured.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Make the refresh function available globally so ZipcodeUpload can call it
  React.useEffect(() => {
    window.refreshUploadHistory = fetchUploadHistory;
    
    // Clean up the global function when component unmounts
    return () => {
      window.refreshUploadHistory = undefined;
    };
  }, []);

  useEffect(() => {
    fetchUploadHistory();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const displayedUploads = uploads.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2,
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Upload History</Typography>
          
          <Tooltip title="Refresh upload history">
            <IconButton 
              onClick={fetchUploadHistory} 
              disabled={refreshing}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ZipcodeUpload />
      </Box>
      
      {error && (
        <Box sx={{ color: 'error.main', p: 2, mb: 2 }}>
          <Typography>{error}</Typography>
        </Box>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Last Modified</strong></TableCell>
                <TableCell><strong>Uploaded By</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedUploads.map((upload, index) => (
                <TableRow key={index}>
                  <TableCell>{upload.filename}</TableCell>
                  <TableCell>{new Date(upload.uploadedAt).toLocaleString()}</TableCell>
                  <TableCell>{upload.uploadedBy || 'Unknown'}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      display: 'inline-block',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: upload.status === 'success' ? 'success.light' : 
                               upload.status === 'failed' ? 'error.light' : 'warning.light',
                      color: upload.status === 'success' ? 'success.dark' : 
                             upload.status === 'failed' ? 'error.dark' : 'warning.dark'
                    }}>
                      {upload.status.charAt(0).toUpperCase() + upload.status.slice(1)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {uploads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No upload history found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={uploads.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10]}
        />
      </Paper>
    </Box>
  );
};

export default UploadHistory;
